import axios from 'axios';

/**
 * Centralized Axios instance with interceptors
 * - Automatically attaches auth token
 * - Handles errors globally
 * - Provides consistent base URL
 */
const apiClient = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const { access_token } = JSON.parse(userData);
        if (access_token) {
          config.headers.Authorization = `Bearer ${access_token}`;
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('user');
      // Only redirect if not already on sign-in page
      if (!window.location.pathname.includes('/sign-in')) {
        window.location.href = '/sign-in';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Helper to get current user ID
 */
export const getCurrentUserId = () => {
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      return JSON.parse(userData).user?.id;
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Helper to check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('user');
};

/**
 * Returns SunEditor image upload config with auth header and full API URL.
 */
export const getEditorUploadConfig = () => {
  const userData = localStorage.getItem('user');
  const headers = {};
  if (userData) {
    try {
      const { access_token } = JSON.parse(userData);
      if (access_token) {
        headers.Authorization = `Bearer ${access_token}`;
      }
    } catch (e) { /* ignore */ }
  }
  return {
    imageUploadUrl: `${process.env.REACT_APP_BACKEND_URL}/api/editor/upload-image`,
    imageUploadHeader: headers,
  };
};

export default apiClient;
